<template>
    <el-popover
        v-model="visible"
        :placement="placement"
        trigger="manual"
        :width="popoverWidth()"
        popper-class="excel-export-popper"
    >
        <div>
            <!-- Colunas declaradas pelo módulo (ExcelHeaderColumn não renderiza nada) -->
            <div style="display: none">
                <slot name="header" />
            </div>

            <div class="d-flex align-items-center justify-content-between">
                <h6 class="font-weight-bold mb-0">{{ title }}</h6>

                <div class="d-flex flex-column justify-content-center align-items-center">
                    <div
                        class="rounded d-flex justify-content-center align-items-center position-relative"
                        :style="{
                            width: '32px',
                            height: '32px',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderColor: '#c2e7b0',
                            backgroundColor: '#f0f9eb'
                        }"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#67C23A" class="bi bi-filetype-xls" viewBox="0 0 16 16">
                            <path fill-rule="evenodd" d="M14 4.5V14a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V4.5h-2A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v9H2V2a2 2 0 0 1 2-2h5.5zM6.472 15.29a1.2 1.2 0 0 1-.111-.449h.765a.58.58 0 0 0 .254.384q.106.073.25.114.143.041.319.041.246 0 .413-.07a.56.56 0 0 0 .255-.193.5.5 0 0 0 .085-.29.39.39 0 0 0-.153-.326q-.152-.12-.462-.193l-.619-.143a1.7 1.7 0 0 1-.539-.214 1 1 0 0 1-.351-.367 1.1 1.1 0 0 1-.123-.524q0-.366.19-.639.19-.272.527-.422.338-.15.777-.149.457 0 .78.152.324.153.5.41.18.255.2.566h-.75a.56.56 0 0 0-.12-.258.6.6 0 0 0-.247-.181.9.9 0 0 0-.369-.068q-.325 0-.513.152a.47.47 0 0 0-.184.384q0 .18.143.3a1 1 0 0 0 .405.175l.62.143q.326.075.566.211a1 1 0 0 1 .375.358q.135.222.135.56 0 .37-.188.656a1.2 1.2 0 0 1-.539.439q-.351.158-.858.158-.381 0-.665-.09a1.4 1.4 0 0 1-.478-.252 1.1 1.1 0 0 1-.29-.375m-2.945-3.358h-.893L1.81 13.37h-.036l-.832-1.438h-.93l1.227 1.983L0 15.931h.861l.853-1.415h.035l.85 1.415h.908L2.253 13.94zm2.727 3.325H4.557v-3.325h-.79v4h2.487z"/>
                        </svg>
                    </div>
                </div>
            </div>

            <el-divider class="my-2"></el-divider>

            <el-form
                :model="form"
                ref="form"
                label-position="top"
                status-icon
                @submit.native.prevent
            >
                <el-row :gutter="20">
                    <el-col>
                        <el-form-item
                            v-if="!form.all_date"
                            prop="periodo"
                            :rules="[{ required: true, message: 'O período é obrigatório', trigger: 'change' }]"
                            class="mb-0"
                        >
                            <label>Período</label>
                            <el-date-picker
                                v-model="form.periodo"
                                unlink-panels
                                size="small"
                                type="daterange"
                                start-placeholder="Início"
                                end-placeholder="Fim"
                                format="dd/MM/yyyy"
                                value-format="yyyy-MM-dd"
                                :style="{ width: '100%' }"
                            />
                        </el-form-item>
                    </el-col>

                    <el-col>
                        <div class="bg-white border rounded-lg p-2 d-flex align-items-center justify-content-between mt-3 mb-2" :style="{ gap: '12px' }">
                            <div class="d-flex align-items-center" :style="{ gap: '8px' }">
                                <el-button :type="form.all_date ? 'success': 'danger'" plain circle size="mini" :style="{ pointerEvents: 'none' }">
                                    <i v-if="form.all_date" class="el-icon-unlock"></i>
                                    <i v-else class="el-icon-lock"></i>
                                </el-button>
                                <span class="mb-0 font-weight-bold">Todo Período?</span>
                            </div>

                            <el-form-item prop="status" class="mb-0">
                                <el-switch 
                                    v-model="form.all_date"
                                    active-text="Sim"
                                    inactive-text="Não"
                                />
                            </el-form-item>
                        </div>
                    </el-col>
                </el-row>
            </el-form>

            <!--
                Filtros do relatório (opcional). O slot recebe `filters`, um objeto
                gerenciado pelo próprio ExcelExport: é enviado ao backend e limpo no reset.
                A key remonta o slot no reset, devolvendo o FilterBuilder ao estado inicial.
            -->
            <div v-if="$scopedSlots.filters" :key="filtersKey" class="mb-3">
                <slot name="filters" :filters="slotFilters" />
            </div>

            <el-row>
                <el-col v-if="isAbleToExport" class="mb-3">
                    <div class="d-flex justify-content-center">
                        <span>
                            <b>
                                {{ data.length }}
                                {{ data.length === 1 ? singularLabel : pluralLabel }}.
                            </b>
                        </span>
                    </div>
                </el-col>

                <el-col>
                    <div class="d-flex justify-content-end">
                        <el-button
                            type="danger"
                            plain
                            size="large"
                            circle
                            :disabled="!isAbleToExport"
                            @click="reset"
                        >
                            <i class="el-icon-delete"></i>
                        </el-button>

                        <!-- 1º clique: só busca os dados (não exporta) -->
                        <el-button
                            v-if="!isAbleToExport"
                            class="rounded-pill"
                            size="large"
                            plain
                            :style="{ width: '100%' }"
                            :disabled="isLoading"
                            @click="prepare"
                        >
                            <i v-if="isLoading" class="el-icon-loading"></i>
                            <i v-else class="el-icon-tickets"></i>
                            <span>Preparar relatório</span>
                        </el-button>

                        <!--
                            2º clique: exporta de fato.
                            export-excel (vue-json-excel) registrado globalmente. Não usamos
                            before-generate: ele roda e já gera o arquivo no mesmo clique.
                        -->
                        <export-excel
                            v-else
                            :key="exportKey"
                            class="el-button el-button--large el-button--success rounded-pill"
                            :style="{ width: '100%' }"
                            :data="data"
                            :fields="fields"
                            :worksheet="worksheet"
                            :name="generatedFileName"
                        >
                            <i class="el-icon-tickets"></i>
                            <span>Exportar relatório</span>
                        </export-excel>
                    </div>
                </el-col>
            </el-row>
        </div>

        <!-- Gatilho (pode ser substituído pelo slot "reference") -->
        <span slot="reference" ref="reference" @click="visible = !visible">
            <slot name="reference">
                <el-button 
                    :style="{
                        height: '32px',
                        paddingBlock: '2px'
                    }"
                    type="success"
                    plain
                    round
                >
                    <i class="el-icon-tickets"></i>
                    {{ buttonLabel }}
                    <i class="el-icon-arrow-down ml-2"></i>
                </el-button>
            </slot>
        </span>
    </el-popover>
</template>

<script>
    /**
     * Uso com FilterBuilder (slot "filters"):
     *
     * <ExcelExport :request="api.list" file-name="revendedoras">
     *     <template #header>
     *         <ExcelHeaderColumn title="Nome" value="nome" />
     *     </template>
     *
     *     <template #filters="{ filters }">
     *         <FilterBuilder inline auto-apply>
     *             <template #list>
     *                 <FilterBuilderDateRange v-model="filters.periodo" title="Período" icon="el-icon-date" />
     *                 <FilterBuilderSelect v-model="filters.status" title="Status" :options="statusOptions" />
     *             </template>
     *         </FilterBuilder>
     *     </template>
     * </ExcelExport>
     *
     * - `filters` é gerenciado pelo ExcelExport: vai junto nos params e é limpo no reset.
     * - Se o FilterBuilder tiver `periodo`, o date picker interno some (igual à prop `filters`).
     * - A prop `filters` continua funcionando e é mesclada com o slot.
     */
    import {
        Popover,
        Button,
        Form,
        FormItem,
        Row,
        Col,
        DatePicker,
        Divider,
        Notification,
        Loading,
        Switch,
    } from 'element-ui';

    export default {
        name: 'ExcelExport',
        provide() {
            return { excelExport: this };
        },
        props: {
            // Filtros do módulo. Se tiver `periodo`, ele é usado; senão o componente pede um.
            filters: { type: Object, default: () => ({}) },
            // (params) => Promise<Array | { data: Array }>. Recebe { ...filters, periodo, excel: true }
            request: { type: Function, required: true },
            // true = envia somente { periodo, excel } (ignora os demais filtros)
            onlyPeriodo: { type: Boolean, default: false },

            title: { type: String, default: 'Relatórios' },
            buttonLabel: { type: String, default: 'Exportar Relatório' },
            fileName: { type: String, default: 'relatorio' },
            worksheet: { type: String, default: 'Relatório' },
            singularLabel: { type: String, default: 'Registro encontrado' },
            pluralLabel: { type: String, default: 'Registros encontrados' },
            // Padrão: 300, ou 640 quando há o slot "filters" (as linhas do FilterBuilder precisam de espaço)
            width: { type: [String, Number], default: null },
            placement: { type: String, default: 'bottom-end' }
        },
        data() {
            return {
                visible: false,
                isLoading: false,
                isAbleToExport: false,
                exportKey: 0, // força remontagem do export-excel
                form: { 
                    periodo: null, 
                    all_date: false
                }, // período próprio (quando filters não tem)
                slotFilters: {}, // valores vindos do slot "filters"
                filtersKey: 0,   // força remontagem do slot "filters" no reset
                data: [],
                // Snapshot das colunas no formato do vue-json-excel, montado no prepare()
                fields: {},
            };
        },
        computed: {
            // filters (prop) + filtros do slot, ignorando condições vazias do slot
            mergedFilters() {
                const fromSlot = Object.keys(this.slotFilters).reduce((acc, key) => {
                    const v = this.slotFilters[key];
                    const empty = v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);
                    if (!empty) acc[key] = v;
                    return acc;
                }, {});
                return { ...this.filters, ...fromSlot };
            },
            hasExternalPeriodo() {
                const p = this.mergedFilters.periodo;
                return Array.isArray(p) ? p.length > 0 && !!p[0] : !!p;
            },
            periodo() {
                return this.hasExternalPeriodo ? this.mergedFilters.periodo : this.form.periodo;
            },
            // Parâmetros enviados ao backend
            params() {
                const base = this.onlyPeriodo ? {} : { ...this.mergedFilters };
                return { ...base, periodo: this.periodo, excel: true };
            },
            generatedFileName() {
                const d = new Date();
                const formatted =
                    String(d.getDate()).padStart(2, '0') + '-' +
                    String(d.getMonth() + 1).padStart(2, '0') + '-' +
                    d.getFullYear();
                return `${this.fileName}-${formatted}`;
            },
        },
        watch: {
            visible(open) {
                if (open) {
                    // fase de captura: roda antes do Vue re-renderizar o alvo do clique
                    document.addEventListener('click', this.onDocumentClick, true);
                } else {
                    document.removeEventListener('click', this.onDocumentClick, true);
                    this.reset();
                }
            },
            // Mudou período/filtro depois de preparar -> o relatório preparado ficou velho
            params: {
                deep: true,
                handler() {
                    if (this.isAbleToExport) this.invalidate();
                },
            },
            "form.all_date": {
                handler() {
                    if (this.isAbleToExport) this.invalidate();
                }
            }
        },
        created() {
            /*
             * ExcelHeaderColumn registrados. NÃO reativo de propósito: o slot "header" é
             * avaliado dentro do render deste componente, então um :callback inline
             * (ex.: v => formatar(v)) vira uma função nova a cada render. Se o render
             * dependesse das colunas, cada render geraria outro -> loop de re-render.
             */
            this.columns = [];
            // Painéis anexados ao <body> pelo Element: clicar neles não fecha o popover
            this.ignoredSelector = '.excel-export-popper, .el-picker-panel';
        },
        beforeDestroy() {
            document.removeEventListener('click', this.onDocumentClick, true);
        },
        methods: {
            /* Registro das colunas */
            register(column) {
                if (!this.columns.includes(column)) this.columns.push(column);
            },
            unregister(column) {
                this.columns = this.columns.filter((c) => c !== column);
            },
            /** Formato do vue-json-excel: { "Título": "campo" | { field, callback } } */
            buildFields() {
                return Object.freeze(this.columns.reduce((acc, col) => {
                    acc[col.title] = col.callback
                        ? { field: col.value, callback: col.callback }
                        : col.value;
                    return acc;
                }, {}));
            },

            /* Fechar ao clicar fora */
            onDocumentClick(e) {
                const target = e.target;
                const reference = this.$refs.reference;
                if (reference && reference.contains(target)) return;
                if (target.closest && target.closest(this.ignoredSelector)) return;
                this.visible = false;
            },

            popoverWidth() {
                if (this.width !== null) return this.width;
                return this.$scopedSlots.filters ? 564 : 300;
            },

            formatPeriodoItem(v) {
                return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)
                    ? v.slice(0, 10).split('-').reverse().join('/')
                    : String(v);
            },

            validate() {
                return new Promise((resolve) => this.$refs.form.validate((valid) => resolve(valid)));
            },

            /**
             * Busca os dados no backend com { ...filters, periodo, excel: true }.
             * Só prepara: a exportação acontece no clique seguinte, no botão do export-excel.
             */
            async prepare() {
                if (this.isAbleToExport || this.isLoading) return;
                if (!(await this.validate())) return;

                this.isLoading = true;
                const elLoading = Loading.service({ customClass: 'loader-indicative-bottom-right' });

                try {
                    const res = await this.request(this.params);
                    const rows = Array.isArray(res) ? res : (res && res.data) || [];

                    this.data = rows;

                    if (!rows.length) {
                        Notification.info({
                            title: 'Sem dados',
                            message: 'Nenhum registro encontrado para o período.',
                        });
                        return;
                    }

                    this.fields = this.buildFields();
                    this.isAbleToExport = true;
                } catch (err) {
                    Notification.error({
                        title: 'Erro',
                        message: 'Não foi possível exportar o relatório!',
                    });
                } finally {
                    elLoading.close();
                    this.isLoading = false;
                }
            },

            /** Descarta o relatório preparado (mantém o período). */
            invalidate() {
                this.data = [];
                this.isAbleToExport = false;
                this.exportKey++;
            },

            /** Volta tudo ao estado inicial. */
            reset() {
                this.form.periodo = null;
                this.slotFilters = {};
                this.filtersKey++;
                this.isLoading = false;
                this.invalidate();
                this.$nextTick(() => {
                    if (this.$refs.form) this.$refs.form.clearValidate();
                });
            },
        },
        components: {
            [Popover.name]: Popover,
            [Button.name]: Button,
            [Form.name]: Form,
            [FormItem.name]: FormItem,
            [Row.name]: Row,
            [Col.name]: Col,
            [DatePicker.name]: DatePicker,
            [Divider.name]: Divider,
            [Switch.name]: Switch,
        },
    };
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/_micro/Excel/ExcelExport.vue